// Customers › All customers in Blueprint (pages/ux-full screens/customers.js): find anyone by name,
// phone, address, or a job or invoice number, or show everyone, homes, businesses, members or those who
// owe money (offered only to those who see what people owe). The answer is ui-web's DataTable on
// customerColumns, drawn quiet; a name opens their record. Add customer (QA-323) opens the new-customer form in the page, started with
// what was typed; for those who can merge, the possible duplicates (BL-029) fold under the list.
// On ui-web's ListPage pattern (patterns/README.md, BL-189): the title block, the search and the Show
// views, the list saying its own empty and failed, the form and the duplicates on catalogued parts.
// Riverstone's Search (Customers.tsx) is the same search (customer-search.ts) drawn its own way.
import { useState } from 'react';
import {
  Area,
  Button,
  customerColumns,
  DataTable,
  DuplicatePairList,
  NewCustomerFields,
} from '@flowology/ui-web/fl';
import { ListPage, type ListStatus } from '@flowology/ui-web/patterns';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { money } from '../review/review-model.ts';
import { formFromSearch, type CustomerForm } from '../shared/new-customer-model.ts';
import { useNewCustomer } from '../shared/use-new-customer.ts';
import { phoneText } from '../types.ts';
import { customerTableRows, LIST_SHOWS } from './bp-customers-model.ts';
import { useCustomerSearch } from './customer-search.ts';
import { PAIRS_NOTE, pairSame, useDuplicatePairs } from './DuplicatePairs.tsx';

export const FIND_LEDE =
  'Find anyone by name, phone, address, or a job or invoice number; their whole history is one click away.';

export function FindCustomers({
  nav,
  onOpen,
  canAdd,
  canMerge,
}: {
  nav: NavProps;
  onOpen: (id: string) => void;
  canAdd: boolean;
  /** customers.merge: the possible duplicates' list (BL-029) */
  canMerge: boolean;
}) {
  const s = useCustomerSearch();
  // Add customer (QA-323): a new company has no calls yet, so the office adds people here or in Book.
  const [adding, setAdding] = useState<CustomerForm | null>(null);
  const crumb = usePageCrumb(nav);
  const head = {
    ...(crumb ? { crumb } : {}),
    title: 'Customers',
    lede: FIND_LEDE,
  };
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        {adding ? (
          <ListPage
            head={head}
            list={{
              title: 'New customer',
              children: <AddCustomer start={adding} onDone={onOpen} onCancel={() => setAdding(null)} />,
            }}
          />
        ) : (
          <Find
            s={s}
            head={head}
            onOpen={onOpen}
            canMerge={canMerge}
            {...(canAdd ? { onAdd: () => setAdding(formFromSearch(s.query)) } : {})}
          />
        )}
      </main>
    </div>
  );
}

function Find({
  s,
  head,
  onOpen,
  onAdd,
  canMerge,
}: {
  s: ReturnType<typeof useCustomerSearch>;
  head: { crumb?: { label: string; href?: string; onPress?: () => void }[]; title: string; lede: string };
  onOpen: (id: string) => void;
  onAdd?: () => void;
  canMerge: boolean;
}) {
  const status: ListStatus = s.error ? 'failed' : !s.searching && !s.rows.length ? 'empty' : 'ready';
  return (
    <ListPage
      head={{
        ...head,
        ...(onAdd
          ? {
              actions: (
                <Button variant="primary" onPress={onAdd}>
                  Add customer
                </Button>
              ),
            }
          : {}),
      }}
      search={{
        label: 'Find a customer',
        value: s.query,
        onChange: s.setQuery,
        placeholder: 'Name, phone, address, job or invoice number',
        autoFocus: true,
      }}
      views={{
        label: 'Show',
        // Owe money only for those who see what people owe (or who chose it)
        items: LIST_SHOWS.filter(([k]) => k !== 'owing' || s.moneyShown || s.show === 'owing').map(
          ([id, label]) => ({ id, label }),
        ),
        value: s.show,
        onChange: (id) => s.setShow(id as typeof s.show),
      }}
      list={{
        title: s.query.trim() ? 'Matches' : (LIST_SHOWS.find(([k]) => k === s.show)?.[1] ?? 'Everyone'),
        ...(s.searching ? {} : { count: s.rows.length }),
        status,
        emptyText: 'No customers match.',
        failedText: s.error ?? '',
        children: (
          <DataTable
            key={s.answered ?? ''}
            columns={customerColumns({ showOwes: s.moneyShown })}
            rows={customerTableRows(s.rows, { phone: phoneText, money })}
            onOpen={(c) => onOpen(c.id)}
            look="quiet"
            first={5}
            {...(s.searching ? { busyText: 'Searching…' } : {})}
          />
        ),
      }}
      {...(canMerge && !s.query.trim() ? { below: <Duplicates onOpen={onOpen} /> } : {})}
    />
  );
}

/** The possible duplicates, folded, for those who can merge; nothing when there are none. */
function Duplicates({ onOpen }: { onOpen: (id: string) => void }) {
  const pairs = useDuplicatePairs();
  if (!pairs?.length) return null;
  return (
    <Area title={`Possible duplicates (${pairs.length})`}>
      <DuplicatePairList
        note={PAIRS_NOTE}
        onOpen={onOpen}
        pairs={pairs.map((p) => ({
          key: `${p.a.id}:${p.b.id}`,
          same: pairSame(p.same),
          people: [p.a, p.b].map((c) => ({ id: c.id, name: c.displayName, number: c.number })),
        }))}
      />
    </Area>
  );
}

/** Add customer: the new-customer form (use-new-customer.ts) on catalogued parts. */
function AddCustomer({
  start,
  onDone,
  onCancel,
}: {
  start: CustomerForm;
  onDone: (id: string) => void;
  onCancel: () => void;
}) {
  const nc = useNewCustomer({ start, pick: 'Open', onDone });
  return (
    <NewCustomerFields
      values={nc.form}
      onChange={nc.change}
      onCheck={nc.check}
      consentNote="Consent (saved with who and when)"
      submitLabel="Add customer"
      busyLabel="Adding the customer…"
      isBusy={nc.busy}
      isSubmitDisabled={nc.blocked}
      error={nc.error}
      onSubmit={() => nc.create()}
      onCancel={onCancel}
      dupe={
        nc.dupe
          ? {
              text: nc.dupe.text,
              pickLabel: nc.dupe.pickLabel,
              onPick: nc.dupe.pick,
              isHeld: nc.held !== null,
              onAddAnyway: () => nc.create(true),
            }
          : null
      }
      unchecked={nc.uncheckedText ? { text: nc.uncheckedText, onAddAnyway: () => nc.create(true) } : null}
    />
  );
}
